import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Grape, Heart, Award, Users } from 'lucide-react';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: <Grape className="h-8 w-8" />,
      title: "4 Generaciones",
      description: "Tradición vitivinícola desde el siglo XIX"
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Pasión por el Vino",
      description: "Del Piamonte italiano a Uruguay"
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Calidad Premium",
      description: "Vinos de alta gama con máximo estándar"
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Experiencia Única",
      description: "Tours, degustaciones y gastronomía"
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Unas Palabras Sobre Nosotros
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{background: 'linear-gradient(90deg, #D4AF37 0%, #C9A961 100%)'}}></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cuatro generaciones que estrechan el Atlántico en un abrazo, uniendo una pasión 
            que nace en el Piamonte italiano y florece en la Cuchilla Pereyra del Uruguay.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-t-4"
              style={{borderTopColor: '#D4AF37'}}
              data-testid={`feature-card-${index}`}
            >
              <div className="mb-4" style={{color: '#D4AF37'}}>{feature.icon}</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 md:p-12">
              <h3 className="text-3xl font-bold text-gray-800 mb-6">Nuestra Historia</h3>
              <div className="space-y-4 text-gray-600">
                <p>
                  <strong style={{color: '#D4AF37'}}>Luigi Spinoglio</strong>, con orígenes en 
                  Casale Monferrato (Piamonte, Italia), llega al Uruguay a finales del siglo XIX 
                  con una historia enraizada en la vid y el vino.
                </p>
                <p>
                  En 1961 su nieto <strong style={{color: '#D4AF37'}}>Ángel</strong> se instala 
                  en la sureña región de <strong>Cuchilla Pereyra</strong>, considerada de máxima 
                  aptitud para la vitivinicultura.
                </p>
                <p>
                  Hoy es <strong style={{color: '#D4AF37'}}>Diego</strong>, cuarta generación Spinoglio 
                  en Uruguay, quien continúa apostando a los máximos estándares de calidad con la 
                  misma pasión y respeto por esta <strong>TIERRA de VINOS</strong>.
                </p>
              </div>
            </div>
            <div className="relative h-64 md:h-auto">
              <img
                src="https://images.unsplash.com/photo-1561906814-23da9a8bfee0?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Bodega Spinoglio"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
